import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { PurchaseConfirmDialog, EmptyState } from "/imports/ui/components/shared";
import usePackWallets from "/imports/hooks/usePackWallets";
import { packMemberPricing, memberPriceNote } from "/imports/libs/memberPricing";
import useUpfrontSlots from "./useUpfrontSlots";
import SlotsToolbar from "./components/SlotsToolbar";
import PicksList from "./components/PicksList";
import SlotComposerSheet from "./components/SlotComposerSheet";
import SlotsStickyBar from "./components/SlotsStickyBar";

/**
 * Rendered by UpfrontSessionsView when the pack's linked service is a
 * private-lesson or court category (classes keep the timetable picker).
 */
function UpfrontSlotsBuilderView({ pkg, opt, locationId, backTo, memberStatus }) {
  const {
    navigate,
    slug,
    currency,
    isCourt,
    noun,
    mode,
    sessionsN,
    minPicks,
    countOk,
    canContinue,
    composeLocationIds,
    composeLocationId,
    setComposeLocationId,
    locationNameById,
    picks,
    setPicks,
    blockedWeeks,
    removePick,
    clearPicks,
    dismissBlockedWeek,
    openBlockedWeek,
    addPick,
    openComposer,
    sheetOpen,
    setSheetOpen,
    sheetDate,
    setSheetDate,
    sheetTrainerId,
    setSheetTrainerId,
    today,
    maxDate,
    dayTrainers,
    slotsLoading,
    groups,
    pickedTimes,
    repeatBusy,
    repeatWeekly,
    confirmOpen,
    setConfirmOpen,
    buying,
    doPurchase,
    confirmLines,
    confirmNote
  } = useUpfrontSlots({ pkg, opt, locationId, backTo });
  // Priced off the composed picks: a book-all pack is member-priced only while
  // every lesson sits at a branch the client is a member at.
  const memberPricing = packMemberPricing({
    pkg,
    opt,
    memberStatus,
    purchaseLocationId: locationId,
    sessionLocationIds: picks.map((p) => p.locationId || locationId || null)
  });
  const effectivePrice = memberPricing.price;
  const memberNote = memberPriceNote({ pricing: memberPricing, opt, currency, nameOf: (id) => locationNameById.get(id) });
  const wallets = usePackWallets({ open: confirmOpen, locationId, price: effectivePrice });

  return (
    <Box sx={{ pb: { xs: "9rem", md: "5.5rem" }, maxWidth: "60rem", mx: "auto" }}>
      <Box mb={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
          <IconButton size="small" onClick={() => navigate(backTo || `/${slug}/plans`)} aria-label="Back to packages" sx={{ ml: -0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            Book your sessions
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {pkg.name}
          {opt.sessionDuration ? ` · ${opt.sessionDuration} min` : ""} —{" "}
          {mode === "all"
            ? `pick a date and time for all ${sessionsN} ${noun}s to book with your purchase.`
            : `pick at least one ${noun} to start with — the rest of your ${sessionsN} sessions can be booked later.`}
        </Typography>
      </Box>

      <SlotsToolbar
        pickCount={picks.length}
        sessionsN={sessionsN}
        countOk={countOk}
        repeatBusy={repeatBusy}
        onAdd={openComposer}
        onRepeatWeekly={repeatWeekly}
        onClear={clearPicks}
      />

      {/* The recommendation to book all sessions has landed once the client picks one. */}
      {mode === "partial" && picks.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          We recommend booking all {sessionsN} sessions now to guarantee your spots — times can fill up, and any sessions you leave for later
          are subject to availability.
        </Alert>
      )}

      {picks.length === 0 && blockedWeeks.length === 0 ? (
        <EmptyState title={`No ${noun}s picked yet`} description={`Tap "Add session" to pick a date and time for your first ${noun}.`} />
      ) : (
        <PicksList
          picks={picks}
          blockedWeeks={blockedWeeks}
          showLocation={composeLocationIds.length > 1}
          onRemove={removePick}
          onOpenBlocked={openBlockedWeek}
          onDismissBlocked={dismissBlockedWeek}
        />
      )}

      <SlotsStickyBar
        currency={currency}
        effectivePrice={effectivePrice}
        regularPrice={memberPricing.regularPrice}
        memberNote={memberNote}
        pickCount={picks.length}
        sessionsN={sessionsN}
        minPicks={minPicks}
        mode={mode}
        blockedCount={blockedWeeks.length}
        canContinue={canContinue && !buying}
        onContinue={() => setConfirmOpen(true)}
      />

      <SlotComposerSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        noun={noun}
        isCourt={isCourt}
        composeLocationIds={composeLocationIds}
        composeLocationId={composeLocationId}
        setComposeLocationId={setComposeLocationId}
        locationNameById={locationNameById}
        sheetDate={sheetDate}
        setSheetDate={setSheetDate}
        today={today}
        maxDate={maxDate}
        dayTrainers={dayTrainers}
        sheetTrainerId={sheetTrainerId}
        setSheetTrainerId={setSheetTrainerId}
        slotsLoading={slotsLoading}
        groups={groups}
        pickedTimes={pickedTimes}
        onAddPick={addPick}
      />

      <PurchaseConfirmDialog
        open={confirmOpen}
        name={pkg?.name}
        lines={confirmLines}
        note={confirmNote}
        location={null}
        price={effectivePrice}
        regularPrice={memberPricing.regularPrice}
        currency={currency || ""}
        wallets={wallets}
        loading={buying}
        onConfirm={doPurchase}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}

export default UpfrontSlotsBuilderView;
